import { createContext, useContext, useMemo, useRef, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@promptgatewaysadmin/ui";
import { useAuth } from "./AuthContext.js";
import { usePortalClient } from "../api/PortalClientProvider.js";
import { getMe, provisionAccount } from "../api/generated/identity.generated.js";
import type { Me, Role } from "../api/generated/identity.generated.js";
import { PortalRequestError } from "../api/client.js";
import { isNotAnError } from "../api/errors.js";
import { readFunnel } from "./persistence.js";
import { qk } from "../queries/keys.js";

/** Identity's 400 for "the GCIP account exists but POST /accounts never reached us". */
function isNoProfile(err: unknown): boolean {
  return (
    err instanceof PortalRequestError &&
    !isNotAnError(err.failure) &&
    err.failure.contractCode === "NO_PROFILE"
  );
}

/**
 * The resolved session. Below RequireOrg, `orgId` and `role` are guaranteed present, so
 * screens never re-derive them or handle a null case a guard already excluded.
 *
 * This duplicates data that also lives in the `me` query. That is intentional and bounded:
 * there is exactly one writer — the query — and the context holds only the resolved,
 * non-null view.
 */
export interface Session {
  me: Me;
  orgId: string | null;
  orgName: string | null;
  role: Role | null;
  plan: string | null;
  emailVerified: boolean;
  hasOrg: boolean;
}

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error("useSession must be used inside <SessionProvider>");
  return s;
}

/** For the shell, which renders above RequireOrg and must tolerate no session. */
export function useOptionalSession(): Session | null {
  return useContext(SessionContext);
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const client = usePortalClient();
  const queryClient = useQueryClient();

  const enabled = auth.status === "authenticated";
  // Self-heal guard: at most one provision attempt per mount. provisionAccount is idempotent
  // on the GCIP uid, so a second attempt would be harmless but pointless.
  const healed = useRef(false);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: qk.me(),
    enabled,
    // Session-cached, but refetched on focus: that is when a role change made by a
    // colleague becomes visible. It is the whole reason role is read from /me rather than
    // from a token claim, which can be an hour stale.
    staleTime: Infinity,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      try {
        return await client.get<Me>(getMe, "shell.me");
      } catch (err) {
        // A Firebase account whose provision never reached the server (a failed preflight, a
        // closed tab between createUser and POST /accounts) is otherwise a dead end: /me
        // 400s NO_PROFILE forever and the only way out was support. Provision once with the
        // intent signup would have sent, then re-read /me — the re-read, not the provision
        // response, is what lets Identity flip email_verified from the token.
        if (isNoProfile(err) && !healed.current) {
          healed.current = true;
          const intent: "personal" | "none" = readFunnel().plan === "team" ? "none" : "personal";
          await client.get(provisionAccount, "auth.signup", { body: { intent } });
          return client.get<Me>(getMe, "shell.me");
        }
        throw err;
      }
    },
  });

  const session = useMemo<Session | null>(() => {
    if (!data) return null;
    // primaryOrgId when set, else the first active membership. An empty array is NOT an
    // error — it is the RequireOrg redirect to /onboarding.
    const membership =
      data.memberships.find((m) => m.orgId === data.primaryOrgId) ??
      data.memberships.find((m) => m.status === "active") ??
      data.memberships[0] ??
      null;
    return {
      me: data,
      orgId: membership?.orgId ?? null,
      orgName: membership?.orgName ?? null,
      role: membership?.role ?? null,
      plan: membership?.plan ?? null,
      emailVerified: data.emailVerified,
      hasOrg: data.memberships.length > 0,
    };
  }, [data]);

  if (auth.status === "loading") return null;

  // Unauthenticated is a legitimate resolved state: the public auth routes render under it.
  if (auth.status === "unauthenticated") {
    return <SessionContext.Provider value={null}>{children}</SessionContext.Provider>;
  }

  // The single blocking fetch in the app. The shell cannot render a nav without a role.
  // No spinner: a full-page spinner is banned on every route, and this resolves in one hop.
  // Text only — a blank page for the hop reads as a broken login, a status line does not.
  if (isPending) {
    return (
      <main id="main" className="pg-empty" role="status" aria-live="polite">
        Loading your account…
      </main>
    );
  }

  if (isError || !session) {
    // The ONLY acceptable full-page error in the portal. An Identity outage means there is
    // no role, no org and no nav — there is nothing honest to render around it.
    return (
      <main id="main" tabIndex={-1} className="pg-empty" role="alert">
        <h1 className="pg-empty__title">We couldn&apos;t load your account.</h1>
        <p className="pg-empty__body">
          Nothing was changed. This is usually temporary — try again in a moment.
        </p>
        <div className="pg-empty__actions">
          <Button onClick={() => void refetch()}>Retry</Button>
          <Button
            variant="secondary"
            onClick={() => {
              queryClient.clear();
              void auth.signOut();
            }}
          >
            Sign out
          </Button>
        </div>
      </main>
    );
  }

  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}

/** Test seam: render a screen against a fixed session without a query client round trip. */
export function MockSessionProvider({
  children,
  session,
}: {
  children: ReactNode;
  session: Session | null;
}) {
  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}
